import { useEffect, useState } from "react";
import axios from "axios";
import { AnimatePresence, motion } from "framer-motion";
import { useLanguage } from "@/i18n";
import { AccentText, Reveal } from "@/components/Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const GROUPS = ["tutti", "freschi", "ittico", "carni-salumi", "formaggi", "dispensa", "fingerfood", "no-food"];

export const ProductCatalog = () => {
    const { t } = useLanguage();
    const [products, setProducts] = useState([]);
    const [filter, setFilter] = useState("tutti");
    const [cols, setCols] = useState(4);

    useEffect(() => {
        const update = () => setCols(window.innerWidth >= 1024 ? 4 : window.innerWidth >= 768 ? 3 : 2);
        update();
        window.addEventListener("resize", update);
        return () => window.removeEventListener("resize", update);
    }, []);

    useEffect(() => {
        axios
            .get(`${API}/products`)
            .then((res) => setProducts(res.data))
            .catch(() => setProducts([]));
    }, []);

    const visible = filter === "tutti" ? products : products.filter((p) => p.group === filter);

    return (
        <section id="prodotti" data-testid="catalog-section" className="bg-[#21252f]">
            <div className="mx-auto max-w-[1600px] px-5 py-24 md:px-12 md:py-32 lg:px-24">
                <Reveal>
                    <p className="mb-4 font-mono text-[11px] uppercase tracking-[0.3em] text-midora-red" data-testid="catalog-overline">
                        {t("catalog.overline")}
                    </p>
                    <h2 className="font-display text-4xl font-light tracking-tighter sm:text-5xl" data-testid="catalog-heading">
                        <AccentText text={t("catalog.heading")} accentKey="catalog" />
                    </h2>
                    <p className="mt-4 max-w-xl text-sm italic text-midora-red md:text-base" data-testid="catalog-sub">
                        {t("catalog.sub")}
                    </p>
                </Reveal>

                <Reveal delay={0.1}>
                    <div className="scrollbar-none -mx-5 mt-12 flex gap-2 overflow-x-auto px-5 pb-2 md:mx-0 md:flex-wrap md:px-0" data-testid="catalog-filters">
                        {GROUPS.map((g) => (
                            <button
                                key={g}
                                data-testid={`catalog-filter-${g}`}
                                onClick={() => setFilter(g)}
                                className={`shrink-0 border px-5 py-2.5 font-mono text-[10px] uppercase tracking-[0.2em] transition-colors duration-300 ${
                                    filter === g
                                        ? "border-midora-red bg-midora-red text-white"
                                        : "border-white/15 text-white/50 hover:border-white/40 hover:text-white"
                                }`}
                            >
                                {t(`catalog.groups.${g}`)}
                            </button>
                        ))}
                    </div>
                </Reveal>

                <motion.div layout className="mt-12 grid grid-cols-2 gap-px border border-white/10 bg-white/10 md:grid-cols-3 lg:grid-cols-4">
                    <AnimatePresence mode="popLayout">
                        {visible.map((p, i) => {
                            const name = t(`catalog.categories.${p.slug}.name`);
                            const desc = t(`catalog.categories.${p.slug}.desc`);
                            return (
                                <motion.article
                                    layout
                                    key={p.slug}
                                    initial={{ opacity: 0, scale: 0.96 }}
                                    animate={{ opacity: 1, scale: 1 }}
                                    exit={{ opacity: 0, scale: 0.96 }}
                                    transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
                                    className="group relative overflow-hidden bg-[#1C2029]"
                                    data-testid={`product-card-${p.slug}`}
                                >
                                    <div className="relative aspect-[4/5] overflow-hidden">
                                        <img
                                            src={p.image}
                                            alt={name}
                                            loading="lazy"
                                            className="h-full w-full object-cover transition-transform duration-[1.2s] ease-out group-hover:scale-110"
                                        />
                                        <div className="absolute inset-0 bg-gradient-to-t from-[#151921] via-transparent to-transparent opacity-90" />
                                        {p.featured && (
                                            <span className="absolute left-4 top-4 bg-midora-red px-3 py-1 font-mono text-[9px] uppercase tracking-[0.2em] text-white">
                                                {t("spotlight.badge")}
                                            </span>
                                        )}
                                        <span className="absolute right-4 top-4 font-mono text-[10px] tracking-[0.2em] text-white/40">
                                            {String(i + 1).padStart(2, "0")}
                                        </span>
                                    </div>
                                    <div className="absolute inset-x-0 bottom-0 p-5">
                                        <h3 className="font-display text-xl font-light tracking-tight text-white md:text-2xl">{name}</h3>
                                        <p className="mt-1 max-h-0 overflow-hidden text-xs text-white/55 opacity-0 transition-all duration-500 group-hover:max-h-16 group-hover:opacity-100">
                                            {desc}
                                        </p>
                                        <span className="mt-3 block h-px w-8 bg-midora-red transition-all duration-500 group-hover:w-full" />
                                    </div>
                                </motion.article>
                            );
                        })}
                    </AnimatePresence>
                    {Array.from({ length: (cols - (visible.length % cols)) % cols }).map((_, i) => (
                        <div
                            key={`brand-filler-${i}`}
                            data-testid={`catalog-filler-${i}`}
                            className="group relative flex items-center justify-center overflow-hidden bg-[#1C2029]"
                        >
                            <div className="aspect-[4/5] w-full" />
                            <img
                                src="/assets/logo-white.png"
                                alt="Midora Atelier Gastronomico"
                                className="absolute inset-0 m-auto w-2/3 opacity-20 transition-opacity duration-700 group-hover:opacity-60"
                            />
                            <span className="absolute bottom-5 left-5 h-1.5 w-1.5 rounded-full bg-midora-red" />
                        </div>
                    ))}
                </motion.div>
            </div>
        </section>
    );
};
